import { Box, Tr, Td } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import ChevronDown from 'components/Icons/ChevronDown';
import ChevronRight from 'components/Icons/ChevronRight';
import {
  getDashboardBlocks,
  getEditInstrumentData,
  handleSelectedRow,
} from 'redux/DashboardSliceV2';
import { getDataTypeBasedValuewithNewValue, getSmartDecimalPlaces } from 'utils/FormatValues';

export interface VarianceRowProperties {
  row: any;
  columns: string[];
}

const VarianceRow = ({ row, columns }: VarianceRowProperties) => {
  const percentageColumn: any = { '▲PY%': true, '▲PL%': true };
  const dispatch = useDispatch();
  const editInstrumentData = useSelector(getEditInstrumentData);
  const modalBlocks = useSelector(getDashboardBlocks);
  const dataType = useMemo(() => {
    try {
      const foundBlock: any = modalBlocks.find((block: any) => {
        return block.block_id === row.indicator.block_id;
      });
      if (foundBlock?.indicators) {
        const indicator = foundBlock?.indicators.find((ind: any) => {
          return row?.indicator?.indicator_id === ind.indicator_id;
        });
        return {
          formatProperties: indicator?.data_format_properties,
          data_type: indicator?.data_format,
        };
      }
    } catch {
      return null;
    }
    return null;
  }, [row, modalBlocks]);

  const [collapsed, setCollapsed] = useState(row.isCollapsed);

  const onToggleCollapse = () => {
    if (editInstrumentData) {
      dispatch(handleSelectedRow({ indicator_id: row.indicator_id }));
    }
    setCollapsed((previousState: any) => !previousState);
  };

  const getBackgroundStyle = (
    output_format: any,
    key: string,
    isExpandedColumn: boolean,
    isSubRow?: boolean,
    isActualValue?: boolean,
  ) => {
    // Same purple ramp DashboardTableRow uses, value for value. Variance was on
    // a teal/cyan ramp of its own, so two instruments showing the same kind of
    // data on one dashboard were tinted from two unrelated palettes.
    const subTotalIndicator = isSubRow ? '#F5F0FF' : '#E9D8FF';
    const regularIndicator = '#F5F0FF';
    const subTotalIndicatorTimeExpansion = isSubRow ? '#EDE4FF' : '#D6BBFB';
    const regularIndicatorTimeExpansion = '#EDE4FF';
    if (output_format) {
      return {
        textAlign: output_format?.textAlign,
        backgroundColor: output_format?.cellColor,
        color: output_format?.textColor,
        textDecoration: output_format?.textDecoration,
        fontStyle: output_format?.textDecoration,
        fontWeight: output_format?.textDecoration === 'bold' ? '700' : '400',
      };
    }

    if (isExpandedColumn) {
      return {
        background: isActualValue
          ? output_format === 'subtotal'
            ? subTotalIndicatorTimeExpansion
            : regularIndicatorTimeExpansion
          : '#EDE4FF',
      };
    }

    const color = isActualValue ? subTotalIndicator : '#F5F0FF';

    return {
      background: output_format === 'subtotal' ? color : isActualValue ? regularIndicator : '#FFF',
    };
  };

  // Convert hex to RGB for mixing colours based on backend strength tokens.
  // Parses each channel directly from the hex string to avoid bitwise operations.
  const hexToRgb = (hex: string) => {
    const normalized = hex.replace('#', '');
    return {
      r: Number.parseInt(normalized.slice(0, 2), 16),
      g: Number.parseInt(normalized.slice(2, 4), 16),
      b: Number.parseInt(normalized.slice(4, 6), 16),
    };
  };

  // Mixes base and endpoint colours to render a light-to-strong badge background.
  const mixColors = (baseHex: string, targetHex: string, strength: number) => {
    const base = hexToRgb(baseHex);
    const target = hexToRgb(targetHex);
    const clamped = Math.max(0, Math.min(1, strength));
    const mix = {
      r: Math.round(base.r + (target.r - base.r) * clamped),
      g: Math.round(base.g + (target.g - base.g) * clamped),
      b: Math.round(base.b + (target.b - base.b) * clamped),
    };
    return `rgb(${mix.r}, ${mix.g}, ${mix.b})`;
  };

  /**
   * Converts a gamma-encoded sRGB byte (0–255) to a linear-light value.
   * Required by the WCAG relative luminance formula before channel weighting.
   */
  const linearizeSrgb = (channelByte: number) => {
    const c = channelByte / 255;
    return c <= 0.040_45 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };

  /**
   * Returns the WCAG relative luminance (0 = black, 1 = white) for an
   * rgb(...) colour string. Uses proper gamma-corrected channel linearisation
   * so the result is perceptually accurate for any colour.
   */
  const relativeLuminance = (rgbString: string) => {
    const numbers = rgbString.match(/\d+/g);
    if (!numbers || numbers.length < 3) return 1;
    const [r, g, b] = numbers.map(Number);
    return 0.2126 * linearizeSrgb(r) + 0.7152 * linearizeSrgb(g) + 0.0722 * linearizeSrgb(b);
  };

  /**
   * Chooses text colour against a given background.
   *
   * The WCAG equal-contrast crossover is 0.179, but that keeps dark text on
   * deep green (#16A34A, luminance ~0.27) which looks flat. A threshold of
   * 0.35 gives white text once the background is strong enough to look clearly
   * coloured (roughly 75%+ strength on both green and red endpoints), while
   * keeping dark text on the lighter pastel tints. Safe for any future colour:
   * anything darker than a medium tint will use white text automatically.
   */
  const getTextColor = (backgroundRgb: string) =>
    relativeLuminance(backgroundRgb) > 0.35 ? '#111827' : '#FFFFFF';

  // Map backend presentation tokens to UI styles for percentage variance pills.
  const getVarianceBadgeStyles = (token: any) => {
    const strength = token?.colour_strength ?? 0;
    const direction = token?.direction ?? 'neutral';
    if (direction === 'neutral' || strength <= 0) {
      return {
        background: '#FFFFFF',
        color: '#111827',
      };
    }
    const endpoint = direction === 'good' ? '#16A34A' : '#DC2626';
    const background = mixColors('#FFFFFF', endpoint, strength);
    return {
      background,
      color: getTextColor(background),
    };
  };

  const renderVarianceCell = (value: any, column: string, rowData: any) => {
    const isPercentageIndicator = dataType?.data_type === 'Percentage';
    if (percentageColumn[column]) {
      // Wrap placeholder in the same pill shell so it occupies identical height/width
      // to a real pill and aligns correctly in the column.
      const pillShell = (content: string) => (
        <Box
          as='span'
          display='inline-flex'
          alignItems='center'
          justifyContent='center'
          borderRadius='999px'
          px='2'
          py='0.5'
          fontWeight={500}
        >
          {content}
        </Box>
      );
      if (isPercentageIndicator) {
        return pillShell('-');
      }
      if (value === null || value === undefined) {
        return pillShell('-');
      }
      const displayValue = `${Number(value).toFixed(getSmartDecimalPlaces(value))}%`;
      const token = rowData?.variance_presentation?.[column];
      const styles = getVarianceBadgeStyles(token);
      return (
        <Box
          as='span'
          display='inline-flex'
          alignItems='center'
          justifyContent='center'
          borderRadius='999px'
          px='2'
          py='0.5'
          fontWeight={500}
          background={styles.background}
          color={styles.color}
        >
          {displayValue}
        </Box>
      );
    }
    return getDataTypeBasedValuewithNewValue(
      dataType?.data_type ?? '',
      value ?? 0,
      dataType?.formatProperties ?? {},
    );
  };

  return (
    <>
      <Tr key={row.indicator_id} bg={'white'} onClick={onToggleCollapse} height={'8'}>
        <Td
          fontFamily={'regular_roboto'}
          fontSize={'2xs'}
          style={{
            ...(() => {
              const { textAlign, ...rest } = getBackgroundStyle(
                row.indicator?.formatting,
                '-',
                false,
                false,
                true,
              );
              return rest;
            })(),
            cursor: row.isCollapsable ? 'pointer' : 'default',
            paddingLeft: '1rem',
            verticalAlign: 'middle',
            // Freeze the Indicators name column while the rest of the table scrolls right.
            position: 'sticky',
            left: 0,
            zIndex: 1,
            background: 'white',
          }}
          textAlign={'left'}
          borderBottom='1px solid #E4D5FC'
        >
          {row?.children?.length ? collapsed ? <ChevronRight /> : <ChevronDown /> : <></>}
          {row.Indicators || row.indicator?.name || row.indicator?.indicator_name}
        </Td>
        {columns.slice(1).map((column: string, colIndex: number) => (
          <Td
            key={colIndex}
            // textAlign={'left'}
            fontFamily={'regular_roboto'}
            fontSize={'2xs'}
            fontWeight={500}
            textTransform='none'
            borderBottom='1px solid #E4D5FC'
            style={{
              textAlign: row.indicator?.formatting?.textAlign, // property only for first column
              ...getBackgroundStyle(row.indicator?.formatting, '-', false, false, true),
              cursor: row.dim_df ? 'pointer' : 'default',
              paddingLeft: '1rem',
              verticalAlign: 'middle',
            }}
          >
            {renderVarianceCell(row[column], column, row)}
          </Td>
        ))}
      </Tr>
      {!collapsed &&
        row.children &&
        row.children.map((child: any, index: number) => (
          <React.Fragment key={child.Indicators + index}>
            <Tr bg={'#F7FAFC'} height={'9'}>
              <Td
                fontFamily={'regular_roboto'}
                fontSize={'2xs'}
                style={{
                  ...(() => {
                    const { textAlign, ...rest } = getBackgroundStyle(
                      child.indicator?.formatting || row.indicator?.formatting,
                      '-',
                      false,
                      true,
                      true,
                    );
                    return rest;
                  })(),
                  paddingLeft: '1rem',
                  verticalAlign: 'middle',
                  // Freeze the dimension sub-row name cell too.
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  background: '#F7FAFC',
                }}
                textAlign={'left'}
              >
                {child.Indicators}
              </Td>
              {columns.slice(1).map((column: string, colIndex: number) => (
                <Td
                  key={colIndex}
                  // textAlign={'left'}
                  paddingLeft='1rem'
                  verticalAlign='middle'
                  fontFamily={'regular_roboto'}
                  fontSize={'2xs'}
                  style={{
                    textAlign:
                      child.indicator?.formatting?.textAlign ||
                      row.indicator?.formatting?.textAlign,
                    ...getBackgroundStyle(
                      child.indicator?.formatting || row.indicator?.formatting,
                      '-',
                      false,
                      true,
                      true,
                    ),
                    paddingLeft: '1rem',
                    verticalAlign: 'middle',
                  }}
                >
                  {renderVarianceCell(child[column], column, child)}
                </Td>
              ))}
            </Tr>
          </React.Fragment>
        ))}
    </>
  );
};
export default VarianceRow;
